import React, { forwardRef, useRef, useState, useEffect } from 'react';
import { Button } from 'antd';
import { CloseOutlined, SettingOutlined, FullscreenOutlined } from '@ant-design/icons';
import { AllStationsComparison } from '../../components/AllStationsComparison/AllStationsComparison';
import type { StationData } from '../../api/stationObservationsService';
import type { EventWithCircuit } from '../../api/eventsService';

interface StationsComparisonPanelProps {
  /** Current theme */
  theme: 'light' | 'dark';
  /** Array of station data (from selected date range) */
  stations: StationData[];
  /** Selected event for circuit timezone and sessions */
  selectedEvent: EventWithCircuit | null;
  /** Card position for dragging */
  cardPosition: { x: number; y: number } | null;
  /** Whether this card is currently being dragged */
  isDragging: boolean;
  /** Handler for mouse down on the card (for dragging) */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Handler to close the panel */
  onClose: () => void;
  /** Panel size (width, height) */
  size: { width: number; height: number };
  /** Handler for size change (resize) */
  onSizeChange: (size: { width: number; height: number }) => void;
}

/**
 * Stations Comparison Panel
 *
 * A compact map overlay panel that displays the All Stations Comparison chart.
 * Settings are accessed via a settings button that opens a drawer.
 * Chart data is driven by the selected date range (stations passed from parent).
 */
export const StationsComparisonPanel = forwardRef<HTMLDivElement, StationsComparisonPanelProps>(({
  theme,
  stations,
  selectedEvent,
  cardPosition,
  isDragging,
  onMouseDown,
  onClose,
  size,
  onSizeChange,
}, ref) => {
  const circuitTimeZone = selectedEvent?.circuit?.time_zone;
  const sessions = selectedEvent?.sessions ?? [];
  const compactActionsRef = useRef<{
    openSettings: () => void;
    openFullScreen: () => void;
  } | null>(null);

  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const MIN_WIDTH = 380;
  const MIN_HEIGHT = 280;
  const MAX_WIDTH = 900;
  const MAX_HEIGHT = 700;

  const setRefs = (el: HTMLDivElement | null) => {
    containerRef.current = el;
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      (ref as React.RefObject<HTMLDivElement | null>).current = el;
    }
  };

  const handleResizeStart = (handle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeHandle(handle);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
      };
    }
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return;

      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      let newWidth = resizeStartRef.current.width;
      let newHeight = resizeStartRef.current.height;

      switch (resizeHandle) {
        case 'se':
          newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeStartRef.current.width + deltaX));
          newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, resizeStartRef.current.height + deltaY));
          break;
        case 's':
          newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, resizeStartRef.current.height + deltaY));
          break;
        case 'e':
          newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeStartRef.current.width + deltaX));
          break;
        default:
          return;
      }

      onSizeChange({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeHandle(null);
      resizeStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeHandle, onSizeChange]);

  const textColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)';
  const iconColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)';

  const resizeHandleStyle: React.CSSProperties = {
    position: 'absolute',
    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
    zIndex: 1001,
  };

  return (
    <div
      ref={setRefs}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).classList.contains('resize-handle')) return;
        onMouseDown(e);
      }}
      style={{
        position: 'absolute',
        ...(cardPosition
          ? { left: cardPosition.x, top: cardPosition.y }
          : { bottom: 60, right: 24 }),
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        padding: '12px 14px',
        borderRadius: 10,
        zIndex: isDragging || isResizing ? 2000 : 1000,
        minWidth: MIN_WIDTH,
        maxWidth: MAX_WIDTH,
        width: size.width,
        height: size.height,
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)',
        cursor: isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : 'default',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        className="drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          cursor: 'grab',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <span style={{
          fontSize: 12,
          fontWeight: 600,
          color: textColor,
        }}>
          Stations Comparison
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => compactActionsRef.current?.openSettings()}
            title="Chart settings"
            style={{ color: iconColor, minWidth: 24, padding: '0 4px' }}
          />
          <Button
            type="text"
            size="small"
            icon={<FullscreenOutlined />}
            onClick={() => compactActionsRef.current?.openFullScreen()}
            title="Full screen chart"
            style={{ color: iconColor, minWidth: 24, padding: '0 4px' }}
          />
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{ color: iconColor, minWidth: 24, padding: '0 4px' }}
          />
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <AllStationsComparison
          stations={stations}
          circuitTimeZone={circuitTimeZone}
          sessions={sessions}
          variant="compact"
          compactActionsRef={compactActionsRef}
        />
      </div>

      {/* Resize handles - right, bottom, bottom-right corner */}
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('e', e)}
        style={{
          ...resizeHandleStyle,
          top: 0,
          right: 0,
          width: 6,
          height: '100%',
          cursor: 'ew-resize',
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('s', e)}
        style={{
          ...resizeHandleStyle,
          bottom: 0,
          left: 0,
          width: '100%',
          height: 6,
          cursor: 'ns-resize',
        }}
      />
      <div
        className="resize-handle"
        onMouseDown={(e) => handleResizeStart('se', e)}
        style={{
          ...resizeHandleStyle,
          bottom: 0,
          right: 0,
          width: 12,
          height: 12,
          cursor: 'nwse-resize',
          borderBottomRightRadius: 10,
        }}
      />
    </div>
  );
});

StationsComparisonPanel.displayName = 'StationsComparisonPanel';

export default StationsComparisonPanel;
